import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { isPlatformBrowser } from '@angular/common';
import { ContractorApiService } from '../../../core/services/api/contractor-api.service';
import { MachineDetailsTeamInfoDto } from '../../../core/models/machines/machine-details-team-info-dto';
import { DefinitionService } from '../../../core/services/definition/definition.service';
import { ContractorDetailsDto, ContractorMachineDto, ContractorMachineTeamDto } from '../../../core/models/contractors/contractor-details-dto';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { NavigationService } from '../../../core/services/navigation.service';
import { MachineStatus, MachineStatusLabels } from '../../../core/models/machines/machine-status';
import { MatTooltip } from '@angular/material/tooltip';
import { CopyToClipboardButtonComponent } from "../../../core/components/copy-to-clipboard-button/copy-to-clipboard-button.component";

@Component({
  selector: 'app-contractor-details',
  imports: [MatIconModule, MatInputModule, MatSelectModule, FormsModule, MatButtonModule, ReactiveFormsModule, MatCheckboxModule, MatOptionModule, MatProgressSpinnerModule, MatTableModule, MatChipsModule, MatTooltip, CopyToClipboardButtonComponent],
  templateUrl: './contractor-details.component.html',
  styleUrl: './contractor-details.component.scss'
})
export class ContractorDetailsComponent {
  public contractor!: ContractorDetailsDto;
  private destroy$ = new Subject<void>();
  showDeactivatedCheckbox = new FormControl(false);
  isLoading: any;
  machineIsUpdated: boolean = false;
  formChanges: ContractorDetailsDto | undefined;
  isBrowser: boolean;

  initialValue: ContractorDetailsDto | undefined;
  contractorMachinesDataSource: MatTableDataSource<ContractorMachineDto> | undefined;
  displayedColumns: string[] = ["machineId", "machineStatusText", "machineTeam", "machineTeamStatusText"];
  private inactiveMachineStatusText: string = MachineStatusLabels[MachineStatus.Deactivated];

  constructor(private contractorApiService: ContractorApiService,
    public definitionService: DefinitionService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private router: Router,
    private navigationService: NavigationService,
    @Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.contractorMachinesDataSource = new MatTableDataSource();
  }

  async ngOnInit(): Promise<void> {
    if (!this.isBrowser) {
      this.isLoading = false;
      return;
    }

    await this.loadContractor();
    this.contractorMachinesDataSource!.data = this.contractor.machines;
    this.contractorMachinesDataSource?.data.sort((a, b) => (b.machineId || '').localeCompare(a.machineId || ''));
    this.filterMachines(!!this.showDeactivatedCheckbox.value);

    this.showDeactivatedCheckbox.valueChanges.subscribe((showInactive: boolean | null) => {
      this.filterMachines(!!showInactive);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadContractor() {
    if (this.isLoading) return;
    this.isLoading = true;
    const idParam = this.route.snapshot.paramMap?.get('contractorId');
    const id = idParam != null ? parseInt(idParam) : 0;

    this.contractor = await firstValueFrom(this.contractorApiService.getContractor(id));
    this.isLoading = false;
  }

  haveItems(items: MachineDetailsTeamInfoDto[]): boolean {
    return items.some(i => i.id);
  }

  getMachineNamesAsString(attributes: ContractorMachineTeamDto[]): string {
    return attributes.map(attribute => attribute.machineTeamName).join(', ');
  }

  getMachineTeamIdsAsString(attributes: ContractorMachineTeamDto[]): string {
    return attributes.map(attribute => attribute.machineTeamId).join(', ');
  }

  getTeamStatusTextAsString(attributes: ContractorMachineTeamDto[]): string {
    return attributes.map(attribute => attribute.statusText).join(', ');
  }

  navigateToMachine(id: number) {
    this.navigationService.navigateToMachine(id);
  }

  navigateToTeam(attributes: ContractorMachineDto[]) {
    const teamInternalId = attributes.find(attr => attr.id)?.id;
    if (teamInternalId) {
      this.navigationService.navigateToTeamById({ id: teamInternalId });
    }
  }

  private filterMachines(showInactive: boolean) {
    if (!this.contractor.machines) {
      return;
    }

    if (showInactive) {
      this.contractorMachinesDataSource!.data = this.contractor.machines;
    } else {
      this.contractorMachinesDataSource!.data = this.contractor.machines.filter(
        m => m.statusText !== this.inactiveMachineStatusText
      );
    }
  }

  goBack() {
    if (isPlatformBrowser(this.platformId)) {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        this.router.navigate(['/contractors']);
      }
    } else {
      this.router.navigate(['/contractors']);
    }
  }
}
